import { Upload, Download, FileText, Check, ChevronRight, Paperclip, X } from 'lucide-react'
import { FormModal } from '@components/branded-modal'
import { useCallback, useRef, useState } from 'react'
import { cn } from 'lib/utils'
import { CLIENT_LIST_UPLOAD_LIMIT, TEXT_CONSTANTS, FILE_NAME_LIMIT_IN_CHARS } from 'constants'

const SECTION_LABEL = 'text-[12px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]'
const BTN_GHOST =
  'inline-flex h-10 items-center justify-center rounded-lg border border-[#D5DAE1] bg-white px-5 text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F1F4F8]'
const BTN_PRIMARY_BASE = 'inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-5 text-sm font-semibold transition-colors'
const BTN_PRIMARY_ON = 'bg-[#0A1628] text-white hover:bg-[#0A1628]/90'
const BTN_PRIMARY_OFF = 'cursor-not-allowed bg-[#E5E8EC] text-[#9AA4B2]'

export const ImportCSVComponent = ({
  downloadCSV,
  handleValidateCSV,
  CSVValidationResponse,
  activeStep,
  setActiveStep,
  file,
  setFile,
  open,
  onClose,
  handleCSVUpload,
  csvValid,
  IMPORT_CLIENT_STEPPER,
  GENERIC_MESSAGES,
  CSVUploadResponse,
  setCsvValid,
  fileUploadErrorMessage,
  setFileUploadErrorMessage,
  title,
  subtitle,
  icon = Upload,
  templateFileName = 'template.csv',
  templateDescription = 'Pre-formatted columns with the required headers.',
  uploadHint,
  uploadVariant = 'dropzone',
  stepLabels = ['Upload File', 'Review & Import']
}) => {
  const inputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)

  const processFile = useCallback(
    (selected) => {
      if (!selected) return
      if (!selected.name?.toLowerCase().endsWith('.csv')) {
        return setFileUploadErrorMessage('Please upload a .csv file.')
      }
      if (selected.size > CLIENT_LIST_UPLOAD_LIMIT) {
        return setFileUploadErrorMessage(TEXT_CONSTANTS.FILE_LIMIT_EXCEEDED)
      }
      if (selected.name.length > FILE_NAME_LIMIT_IN_CHARS) {
        return setFileUploadErrorMessage(TEXT_CONSTANTS.FILE_NAME_SIZE_EXCEEDED)
      }
      setFile(selected)
      setCsvValid(true)
      setFileUploadErrorMessage('')
    },
    [setFile, setCsvValid, setFileUploadErrorMessage]
  )

  const handleInputChange = useCallback(
    (e) => {
      processFile(e.target.files?.[0])
      // Reset so the same file can be re-selected.
      e.target.value = null
    },
    [processFile]
  )

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      setIsDragging(false)
      processFile(e.dataTransfer?.files?.[0])
    },
    [processFile]
  )

  const handleRemoveFile = useCallback(() => {
    setFile(null)
    setFileUploadErrorMessage('')
  }, [setFile, setFileUploadErrorMessage])

  const isUploadStep = activeStep === IMPORT_CLIENT_STEPPER[0]
  const isReviewStep = activeStep === IMPORT_CLIENT_STEPPER[1]
  const activeIndex = isUploadStep ? 0 : 1
  const nextDisabled = Boolean(fileUploadErrorMessage) || !file
  const hint = uploadHint || `CSV up to ${Math.round(CLIENT_LIST_UPLOAD_LIMIT / (1024 * 1024))} MB`
  const instructions = GENERIC_MESSAGES?.CLIENT_IMPORT_INSTRUCTIONS || []

  const footer = (
    <div className='flex items-center justify-between'>
      <span className='text-[13px] text-[#6B7280]'>{isUploadStep ? 'Step 1 of 2' : 'Step 2 of 2'}</span>
      <div className='flex items-center gap-2'>
        {isUploadStep ? (
          <>
            <button type='button' className={BTN_GHOST} onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              disabled={nextDisabled}
              className={cn(BTN_PRIMARY_BASE, nextDisabled ? BTN_PRIMARY_OFF : BTN_PRIMARY_ON)}
              onClick={handleValidateCSV}
            >
              Next <ChevronRight className='h-4 w-4' />
            </button>
          </>
        ) : isReviewStep ? (
          <>
            {csvValid ? (
              <button type='button' className={BTN_GHOST} onClick={onClose}>
                Cancel
              </button>
            ) : (
              <button
                type='button'
                className={BTN_GHOST}
                onClick={() => {
                  setActiveStep((step) => step - 1)
                  setCsvValid(true)
                }}
              >
                Previous
              </button>
            )}
            <button
              type='button'
              disabled={!csvValid}
              className={cn(BTN_PRIMARY_BASE, !csvValid ? BTN_PRIMARY_OFF : BTN_PRIMARY_ON)}
              onClick={handleCSVUpload}
            >
              Confirm
            </button>
          </>
        ) : (
          <button type='button' className={cn(BTN_PRIMARY_BASE, BTN_PRIMARY_ON)} onClick={onClose}>
            Dismiss
          </button>
        )}
      </div>
    </div>
  )

  return (
    <FormModal open={open} onClose={onClose} title={title} subtitle={subtitle} icon={icon} footer={footer}>
      {/* Stepper strip — full-bleed under the header */}
      <div className='-mx-5 -mt-4 mb-5 border-b border-[#EBEDF0] bg-[#F8FAFC] px-5 py-3'>
        <div className='flex items-center'>
          {stepLabels.map((label, i) => {
            const reached = i <= activeIndex
            const done = i < activeIndex
            return (
              <div key={label} className={cn('flex items-center gap-2.5', i > 0 && 'flex-1')}>
                {i > 0 && <div className='mx-3 h-px flex-1 bg-[#E1E5EA]' />}
                <span
                  className={cn(
                    'inline-flex h-7 w-7 flex-none items-center justify-center rounded-full text-[13px] font-semibold',
                    reached ? 'bg-[#0A1628] text-white' : 'border border-[#CBD2DC] bg-white text-[#9AA4B2]'
                  )}
                >
                  {done ? <Check className='h-3.5 w-3.5' /> : i + 1}
                </span>
                <span className={cn('whitespace-nowrap text-sm', reached ? 'font-semibold text-[#0A1628]' : 'text-[#9AA4B2]')}>
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {isUploadStep ? (
        <>
          {/* Template */}
          <p className={cn(SECTION_LABEL, 'mb-2')}>STEP 1 · START FROM THE TEMPLATE</p>
          <div className='mb-6 flex items-center gap-3 rounded-xl border border-[#E4E8EE] bg-[#F8FAFC] p-3'>
            <span className='inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-[#E4E8EE] bg-white'>
              <FileText className='h-5 w-5 text-[#5B6472]' />
            </span>
            <div className='min-w-0 flex-1'>
              <div className='truncate text-[15px] font-semibold text-[#0A1628]'>{templateFileName}</div>
              <div className='truncate text-[13px] text-[#6B7280]'>{templateDescription}</div>
            </div>
            <button
              type='button'
              onClick={() => downloadCSV({ template: true })}
              className='inline-flex h-10 flex-none items-center gap-2 rounded-lg border border-[#D5DAE1] bg-white px-4 text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F1F4F8]'
            >
              <Download className='h-4 w-4' /> Download
            </button>
          </div>

          {/* Checklist */}
          <p className={cn(SECTION_LABEL, 'mb-3')}>BEFORE YOU UPLOAD</p>
          <ul className='mb-6 space-y-2.5'>
            {instructions.map((instruction, index) => (
              <li key={index} className='flex items-start gap-2.5'>
                <span className='mt-0.5 inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-[#DCE7FF]'>
                  <Check className='h-3 w-3 text-[#2F6BFF]' />
                </span>
                <span className='text-[14px] leading-snug text-[#374151]'>{instruction}</span>
              </li>
            ))}
          </ul>

          {/* Upload control */}
          <p className={cn(SECTION_LABEL, 'mb-3')}>UPLOAD YOUR FILE</p>
          {uploadVariant === 'row' ? (
            <>
              {!file?.name ? (
                <label
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-4 rounded-xl border bg-white px-4 py-3 transition-colors',
                    isDragging ? 'border-[#2F6BFF] bg-[#F2F6FF]' : 'border-[#D1D5D8] hover:bg-[#F8FAFC]'
                  )}
                >
                  <span className='flex min-w-0 items-center gap-3'>
                    <span className='inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E8EFFF]'>
                      <Upload className='h-5 w-5 text-[#0A1628]' strokeWidth={1.75} />
                    </span>
                    <span className='min-w-0'>
                      <span className='block text-[15px] font-bold text-[#0A1628]'>Drag &amp; drop or browse your CSV</span>
                      <span className='mt-0.5 block text-[13px] text-[#6B7280]'>{hint}</span>
                    </span>
                  </span>
                  <span className='shrink-0 rounded-full bg-[#1F6FEB] px-5 py-2 text-[12px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1A5FD0]'>
                    Browse
                  </span>
                  <input ref={inputRef} type='file' hidden accept='.csv' aria-label='File Upload' onChange={handleInputChange} />
                </label>
              ) : (
                <div className='flex items-center justify-between gap-4 rounded-xl border border-[#D1D5D8] bg-white px-4 py-3'>
                  <span className='flex min-w-0 items-center gap-3'>
                    <span className='inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E8EFFF]'>
                      <Paperclip className='h-5 w-5 text-[#1F6FEB]' strokeWidth={1.75} />
                    </span>
                    <span className='min-w-0'>
                      <span className='block truncate text-[15px] font-bold text-[#0A1628]' title={file.name}>
                        {file.name}
                      </span>
                      {file.size != null && (
                        <span className='mt-0.5 block font-mono text-[13px] text-[#6B7280]'>
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </span>
                      )}
                    </span>
                  </span>
                  <button
                    type='button'
                    onClick={handleRemoveFile}
                    aria-label='Remove file'
                    className='inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#475569] transition-colors hover:bg-[#F1F4F8]'
                  >
                    <X className='h-4 w-4' />
                  </button>
                </div>
              )}
              {fileUploadErrorMessage && <p className='mt-2 text-[13px] text-[#B91C1C]'>{fileUploadErrorMessage}</p>}
            </>
          ) : (
            <div
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
                isDragging ? 'border-[#2F6BFF] bg-[#F2F6FF]' : 'border-[#CBD2DC] bg-white'
              )}
            >
              <span className='mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#E8EEFB]'>
                <Upload className='h-5 w-5 text-[#2F6BFF]' />
              </span>
              <div className='text-[16px] font-semibold text-[#0A1628]'>Drag &amp; drop your CSV here</div>
              <div className='mt-1 text-[14px] text-[#6B7280]'>
                or{' '}
                <button type='button' onClick={() => inputRef.current?.click()} className='font-semibold text-[#2F6BFF] hover:underline'>
                  browse
                </button>{' '}
                to choose a file
              </div>
              <div className='mt-2 text-[12px] text-[#9AA4B2]'>{hint}</div>
              <input ref={inputRef} type='file' hidden accept='.csv' aria-label='File Upload' onChange={handleInputChange} />
              {file?.name && <div className='mt-3 text-[13px] font-medium text-[#0A1628]'>Selected: {file.name}</div>}
              {fileUploadErrorMessage && <div className='mt-2 text-[13px] text-[#B91C1C]'>{fileUploadErrorMessage}</div>}
            </div>
          )}
        </>
      ) : isReviewStep ? (
        <>
          <p className={cn(SECTION_LABEL, 'mb-3')}>{csvValid ? 'Ready to import' : 'Resolve the following issues'}</p>
          <ul className='space-y-2'>
            {CSVValidationResponse?.map((message, index) => (
              <li
                key={index}
                className={cn('flex items-start gap-2 text-[14px] leading-snug', csvValid ? 'text-[#374151]' : 'text-[#B91C1C]')}
              >
                <span className='mt-0.5'>•</span>
                <span>{message}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className='rounded-lg bg-[#0A1628] px-4 py-3'>
          <ul className='space-y-2'>
            {CSVUploadResponse.map((message, index) => (
              <li key={index} className='text-[14px] leading-snug text-white'>
                {message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </FormModal>
  )
}
